/**
 * i18n Utilities from app-shell
 *
 * This module exports i18n configuration utilities that customer applications
 * can use to create their own i18n instances (apps/<customer>/src/plugins/).
 *
 * The actual i18n instance is created in each customer app to avoid
 * circular dependencies between app-shell and features.
 */
import type { App } from 'vue';
import i18next from 'i18next';
import I18NextVue from 'i18next-vue';
import LanguageDetector from 'i18next-browser-languagedetector';
import merge from 'lodash/merge';

import appShellEn from '../locales/en.json';
import appShellDe from '../locales/de.json';
import authEn from '../auth0/locales/en.json';
import authDe from '../auth0/locales/de.json';

// Merged locales with auth namespaced
export const appEn = { ...appShellEn, auth: authEn };
export const appDe = { ...appShellDe, auth: authDe };

export interface I18nConfig {
  baseTranslations: {
    en: Record<string, unknown>;
    de?: Record<string, unknown>;
  };
  featureLocales: Array<{
    en: Record<string, unknown>;
    de?: Record<string, unknown>;
  }>;
}

/**
 * Creates an i18n plugin with the given configuration
 * Usage in customer app:
 *
 * import { createI18nPlugin, appEn, appDe } from '@peanuds/app-shell';
 * import { authLocales } from '@peanuds/app-shell';
 * import { moneyLocales } from '@peanuds/money';
 * import baseEn from './translations/en.json';
 *
 * const i18nPlugin = createI18nPlugin({
 *   baseTranslations: { en: baseEn, de: {} },
 *   featureLocales: [
 *     { en: appEn, de: appDe },
 *     authLocales,
 *     moneyLocales,
 *   ],
 * });
 *
 * app.use(i18nPlugin);
 */
export function createI18nPlugin(config: I18nConfig) {
  return {
    install(app: App) {
      const resources = {
        en: { translation: config.baseTranslations.en },
        de: { translation: config.baseTranslations.de || {} },
      };

      // Merge all feature locales
      for (const locales of config.featureLocales) {
        merge(resources.en.translation, locales.en);
        if (locales.de) {
          merge(resources.de.translation, locales.de);
        }
      }

      i18next
        .use(LanguageDetector)
        .init({
          debug: false,
          fallbackLng: 'en',
          returnEmptyString: false,
          resources,
          interpolation: {
            escapeValue: false,
          },
          detection: {
            order: ['querystring', 'localStorage'],
            lookupQuerystring: 'lang',
            lookupLocalStorage: 'lang',
            caches: ['localStorage'],
          },
        })
        .catch(console.error);

      app.use(I18NextVue, { i18next });
    },
  };
}
